/**
 * Draws one widget from its query result. Content widgets (header, text,
 * image) need no result. A source with nothing attached says so plainly —
 * never a row of fake zeros.
 */

import type { ReactNode } from 'react';

import { Sparkline } from '../charts.tsx';
import { bucketLabel, formatChange, formatValue } from './format.ts';
import { useRollup } from './rollup-context.ts';
import { useAudience } from './audience.ts';
import { useChangeFormat } from './change-format.ts';
import { FormattedText, PageList, useReportPages } from './PageArt.tsx';
import { titleText, useReportFacts } from './report-facts.ts';
import type { CatalogueMetric, CatalogueSource, Widget, WidgetResult } from './reports-api.ts';
import { ChartBody, GaugeBody, SparklineBody } from './VisualBody.tsx';

interface Props {
  widget: Widget;
  result: WidgetResult | undefined;
  source: CatalogueSource | undefined;
  height: number;
}

function metricDefs(widget: Widget, source: CatalogueSource | undefined): CatalogueMetric[] {
  return (widget.data.metrics ?? [])
    .map((key) => source?.metrics.find((metric) => metric.key === key))
    .filter((metric): metric is CatalogueMetric => Boolean(metric));
}

function titleOf(
  widget: Widget,
  metrics: CatalogueMetric[],
  source: CatalogueSource | undefined,
): string {
  if (widget.display.title) return widget.display.title;
  const names = metrics.map((metric) => metric.label).join(' & ');
  const by = widget.data.dimension
    ? ` by ${source?.dimensions.find((d) => d.key === widget.data.dimension)?.label.toLowerCase() ?? widget.data.dimension}`
    : '';
  return names ? `${names}${by}` : (source?.label ?? 'Widget');
}

function StateNote({
  result,
  source,
}: {
  result: WidgetResult | undefined;
  source: CatalogueSource | undefined;
}): ReactNode {
  const audience = useAudience();
  const isRollup = useRollup() !== null;
  if (!result) return <div className="rw-note">Loading…</div>;
  if (result.state === 'NOT_CONNECTED' && audience === 'client') {
    return <div className="rw-note">No {source?.label ?? ''} numbers to show yet.</div>;
  }
  if (result.state === 'NOT_CONNECTED')
    return (
      <div className="rw-note">
        {isRollup
          ? `None of these clients has a ${source?.label ?? 'data source'} account attached.`
          : `Attach a ${source?.label ?? 'data source'} account to this client to see live numbers.`}
      </div>
    );
  // A live source refused (access revoked, Google down): say so, never pretend it is zero.
  if (result.state === 'ERROR')
    return (
      <div className="rw-note" role="alert">
        {audience === 'client'
          ? `${source?.label ?? 'This source'} could not be reached just now.`
          : (result.error ?? `${source?.label ?? 'This source'} could not be reached.`)}
      </div>
    );
  return <div className="rw-note">No {source?.label ?? ''} data in this period.</div>;
}

function MetricBody({
  widget,
  result,
  metric,
}: {
  widget: Widget;
  result: WidgetResult;
  metric: CatalogueMetric;
}): ReactNode {
  const changeFormat = useChangeFormat();
  const entry = result.metrics.find((item) => item.key === metric.key);
  const change =
    widget.display.show_comparison === false
      ? null
      : formatChange(entry?.change ?? null, metric.higher_is_better, {
          as: changeFormat,
          format: metric.format,
        });
  const trend = result.series
    .map((point) => point.values[metric.key])
    .filter((value): value is number => typeof value === 'number');
  return (
    <div className="rw-metric">
      <div className="rw-metric-value mono">{formatValue(entry?.value, metric.format)}</div>
      <div className="rw-metric-foot">
        {change && (
          <span
            className={`rw-change rw-change-${change.tone}`}
            title={
              entry?.previous !== null
                ? `Previous: ${formatValue(entry?.previous, metric.format)}`
                : undefined
            }
          >
            {change.text}
          </span>
        )}
        {widget.display.show_sparkline !== false && (
          <Sparkline values={trend} width={88} height={22} />
        )}
      </div>
    </div>
  );
}

function GoalBody({
  widget,
  result,
  metric,
}: {
  widget: Widget;
  result: WidgetResult;
  metric: CatalogueMetric;
}): ReactNode {
  const value = result.metrics.find((item) => item.key === metric.key)?.value ?? null;
  const target = widget.data.target ?? 0;
  const share = target > 0 && value !== null ? Math.min(1, value / target) : 0;
  return (
    <div className="rw-goal">
      <div className="rw-metric-value mono">{formatValue(value, metric.format)}</div>
      <div
        className="rw-goal-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(share * 100)}
      >
        <span className="rw-goal-fill" style={{ width: `${share * 100}%` }} />
      </div>
      <div className="rw-goal-foot">
        {target > 0
          ? `${Math.round(share * 100)}% of ${formatValue(target, metric.format)}`
          : 'Set a target in Display'}
      </div>
    </div>
  );
}

function TableBody({
  widget,
  result,
  metrics,
  dimensionLabel,
}: {
  widget: Widget;
  result: WidgetResult;
  metrics: CatalogueMetric[];
  dimensionLabel: string;
}): ReactNode {
  const changeFormat = useChangeFormat();
  const rows =
    result.rows ??
    result.series.map((point) => ({
      label: bucketLabel(point.bucket, result.granularity),
      values: point.values,
      compare: null,
    }));
  const totals = Object.fromEntries(result.metrics.map((entry) => [entry.key, entry.value]));
  return (
    <div className="rw-table-wrap">
      <table className="rw-table">
        <thead>
          <tr>
            <th scope="col">{widget.data.dimension ? dimensionLabel : 'Period'}</th>
            {metrics.map((metric) => (
              <th scope="col" key={metric.key} className="num">
                {metric.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              {metrics.map((metric) => {
                const change = row.compare
                  ? formatChange(
                      {
                        absolute: (row.values[metric.key] ?? 0) - (row.compare[metric.key] ?? 0),
                        percent: row.compare[metric.key]
                          ? ((row.values[metric.key] ?? 0) - (row.compare[metric.key] ?? 0)) /
                            Math.abs(row.compare[metric.key] as number)
                          : null,
                      },
                      metric.higher_is_better,
                      { as: changeFormat, format: metric.format },
                    )
                  : null;
                return (
                  <td key={metric.key} className="num mono">
                    {formatValue(row.values[metric.key], metric.format)}
                    {change && widget.display.show_comparison !== false && (
                      <span className={`rw-cell-change rw-change-${change.tone}`}>
                        {change.text}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {widget.display.show_totals !== false && (
          <tfoot>
            <tr>
              <th scope="row">
                Total
                {result.total_rows && result.rows && result.total_rows > result.rows.length
                  ? ` (${result.total_rows} rows)`
                  : ''}
              </th>
              {metrics.map((metric) => (
                <td key={metric.key} className="num mono">
                  {formatValue(totals[metric.key], metric.format)}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function TocBody({ title }: { title?: string | undefined }): ReactNode {
  const pages = useReportPages();
  return (
    <div className="rw-text">
      <h3 className="rw-text-title">{title || 'Contents'}</h3>
      <PageList pages={pages} />
    </div>
  );
}

/** Only https addresses are embedded, in a sandboxed frame that cannot reach this page. */
function EmbedBody({
  url,
  title,
}: {
  url?: string | undefined;
  title?: string | undefined;
}): ReactNode {
  const safe = typeof url === 'string' && /^https:\/\//i.test(url.trim()) ? url.trim() : null;
  if (!safe) return <div className="rw-note">Paste an https address to embed in Display.</div>;
  return (
    <iframe
      className="rw-embed"
      src={safe}
      title={title || 'Embedded content'}
      sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
      referrerPolicy="no-referrer"
      loading="lazy"
    />
  );
}

function TitleBody({ display }: { display: Widget['display'] }): ReactNode {
  const facts = useReportFacts();
  const audience = useAudience();
  const Heading = display.heading ?? 'h3';
  return (
    <Heading
      className={`rw-header rw-header-${display.heading ?? 'h3'}`}
      style={{ textAlign: display.align ?? 'left' }}
    >
      {titleText(display, facts, audience === 'client')}
    </Heading>
  );
}

const EMPTY_NOTE: Partial<Record<Widget['type'], string>> = {
  TEXT: 'Write a note for your client: what changed, what you did, what comes next.',
  AI_SUMMARY: 'Open this widget’s settings and choose Generate summary.',
};

export function WidgetView({ widget, result, source, height }: Props): ReactNode {
  const audience = useAudience();
  if (widget.type === 'HEADER') return <TitleBody display={widget.display} />;
  if (widget.type === 'TEXT' || widget.type === 'AI_SUMMARY') {
    const empty = audience === 'agency' ? (EMPTY_NOTE[widget.type] ?? '') : '';
    return (
      <div className="rw-text">
        {widget.display.title && <h3 className="rw-text-title">{widget.display.title}</h3>}
        <FormattedText text={widget.display.text || empty} />
      </div>
    );
  }
  if (widget.type === 'TOC') return <TocBody title={widget.display.title} />;
  if (widget.type === 'EMBED')
    return <EmbedBody url={widget.display.url} title={widget.display.title} />;
  if (widget.type === 'IMAGE') {
    if (!widget.display.image) return <div className="rw-note">Add an image in Display.</div>;
    return (
      <img
        className={`rw-image rw-image-${widget.display.fit ?? 'contain'}`}
        src={widget.display.image}
        alt={widget.display.alt ?? ''}
      />
    );
  }

  const metrics = metricDefs(widget, source);
  const title = titleOf(widget, metrics, source);
  const body = (() => {
    if (metrics.length === 0)
      return <div className="rw-note">Pick a source and a metric in Data.</div>;
    if (!result || result.state !== 'OK') return <StateNote result={result} source={source} />;
    if (widget.type === 'METRIC')
      return <MetricBody widget={widget} result={result} metric={metrics[0] as CatalogueMetric} />;
    if (widget.type === 'GOAL')
      return <GoalBody widget={widget} result={result} metric={metrics[0] as CatalogueMetric} />;
    if (widget.type === 'GAUGE')
      return <GaugeBody widget={widget} result={result} metric={metrics[0] as CatalogueMetric} />;
    if (widget.type === 'SPARKLINE')
      return <SparklineBody result={result} metric={metrics[0] as CatalogueMetric} />;
    if (widget.type === 'TABLE')
      return (
        <TableBody
          widget={widget}
          result={result}
          metrics={metrics}
          dimensionLabel={
            source?.dimensions.find((item) => item.key === widget.data.dimension)?.label ??
            (widget.data.dimension === 'client' ? 'Client' : 'Name')
          }
        />
      );
    return (
      <ChartBody
        widget={widget}
        result={result}
        metrics={metrics}
        height={Math.max(120, height - 56)}
      />
    );
  })();
  return (
    <figure className="rw" aria-label={title}>
      <figcaption className="rw-title">
        <span>{title}</span>
        {source && <span className="rw-source">{source.label}</span>}
      </figcaption>
      {body}
    </figure>
  );
}
